import React from 'react';
import { useNavigate } from 'react-router-dom';
import { LayoutDashboard, Briefcase, FileText, Wallet, Sparkles, Bell, Users, Settings, ClipboardCheck, LogOut, ShieldCheck, Globe2, Compass, MessageCircle, Mail, BarChart3, Building, Cpu, TrendingUp, FileSignature, ListTodo, Timer, Contact, Receipt, Library, ClipboardList, UserCheck } from 'lucide-react';
import { auth } from '@/lib/api';
import { Link, useLocation } from 'react-router-dom';
import { Input } from '@/components/ui/input';
import { Search, HelpCircle, ChevronDown, ExternalLink } from 'lucide-react';
import { AlertsBell } from '../components/AlertsBell';
import TimerWidget from '../components/TimerWidget';
import GuidedTourOverlay from '../components/GuidedTourOverlay';

const NAV = [
  { label: 'Dashboard', to: '/app', icon: LayoutDashboard, exact: true },
  { label: 'Matters', to: '/app/matters', icon: Briefcase },
  { label: 'Applicants', to: '/app/applicants', icon: UserCheck },
  { label: 'Lodgement', to: '/app/lodgement', icon: FileText },
  { label: 'Documents', to: '/app/documents', icon: FileText },
  { label: 'Trust & Accounting', to: '/app/trust', icon: Wallet },
  { label: 'AI Copilot', to: '/app/copilot', icon: Sparkles },
  { label: 'VETASSESS Tracker', to: '/app/vetassess', icon: ClipboardCheck },
  { label: 'Policy Pulse', to: '/app/policy', icon: Bell },
  { label: 'Migration Intel', to: '/app/intel', icon: Globe2 },
  { label: 'Pathway Advisor', to: '/app/pathways', icon: Compass },
  { label: 'Client Q&A', to: '/app/questions', icon: MessageCircle },
  { label: 'Letters', to: '/app/letters', icon: Mail },
  { label: 'eSign', to: '/app/esign', icon: FileSignature },
  { label: 'Tasks', to: '/app/tasks', icon: ListTodo },
  { label: 'Time tracking', to: '/app/time', icon: Timer },
  { label: 'Quotes', to: '/app/quotes', icon: Receipt },
  { label: 'Intake forms', to: '/app/intake', icon: ClipboardList },
  { label: 'Contacts', to: '/app/contacts', icon: Contact },
  { label: 'Reports', to: '/app/reports', icon: BarChart3, exact: true },
  { label: 'Reports library', to: '/app/reports/library', icon: Library },
  { label: 'Firm analytics', to: '/app/analytics', icon: TrendingUp },
  { label: 'Sponsors', to: '/app/sponsors', icon: Building },
  { label: 'Team & Clients', to: '/app/team', icon: Users },
  { label: 'Settings', to: '/app/settings', icon: Settings },
];

export default function AppLayout({ children }) {
  const loc = useLocation();
  const nav = useNavigate();
  const isActive = (item) => item.exact ? loc.pathname === item.to : loc.pathname.startsWith(item.to);
  const [user, setUser] = React.useState(auth.currentUser());

  React.useEffect(() => {
    if (!auth.isAuthed()) {
      nav('/login');
    }
  }, [nav]);

  const signOut = () => { auth.logout(); nav('/'); };
  const avatar = (user?.name || '?').split(' ').map((s) => s[0]).join('').slice(0, 2).toUpperCase();

  return (
    <div className="flex h-screen bg-[#F4F7FA] text-[#0A0A0A]">
      <aside className="flex w-64 flex-col border-r border-slate-200 bg-[#1A3A5C] text-white">
        <Link to="/app" className="flex items-center gap-3 border-b border-white/10 px-6 py-5">
          <img src="/immipro-logo.png" alt="ImmiPro" className="h-8 w-auto bg-white rounded p-1" />
          <div>
            <div className="font-serif text-lg font-bold">ImmiPro</div>
            <div className="text-[10px] uppercase tracking-widest text-white/50">Staff workspace</div>
          </div>
        </Link>

        <nav className="flex-1 space-y-1 overflow-y-auto px-3 py-4">
          {NAV.map((n) => {
            const Icon = n.icon;
            const active = isActive(n);
            return (
              <Link key={n.to} to={n.to}
                data-testid={`nav-${n.to.replace(/^\/app\/?/, '').replace(/\//g, '-') || 'dashboard'}`}
                className={`flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors ${
                  active ? 'bg-[#2ECC9A]/15 text-[#2ECC9A]' : 'text-white/75 hover:bg-white/5 hover:text-white'
                }`}>
                <Icon className="h-4 w-4" />{n.label}
              </Link>
            );
          })}
          {user?.role === 'super_admin' && (
            <Link
              to="/app/admin"
              data-testid="nav-admin-console"
              className={`flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors ${
                loc.pathname === '/app/admin'
                  ? 'bg-[#2ECC9A]/15 text-[#2ECC9A]'
                  : 'text-white/75 hover:bg-white/5 hover:text-white'
              }`}
            >
              <ShieldCheck className="h-4 w-4" />Admin console
            </Link>
          )}
          {user?.role === 'super_admin' && (
            <Link
              to="/app/admin/elodge"
              data-testid="nav-elodge-fleet"
              className={`flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors ${
                loc.pathname.startsWith('/app/admin/elodge')
                  ? 'bg-[#2ECC9A]/15 text-[#2ECC9A]'
                  : 'text-white/75 hover:bg-white/5 hover:text-white'
              }`}
            >
              <Cpu className="h-4 w-4" />eLodge fleet
            </Link>
          )}
        </nav>

        <div className="border-t border-white/10 p-3">
          <a href="/portal" target="_blank" rel="noreferrer"
            className="mb-2 flex items-center justify-between rounded-md bg-white/5 px-3 py-2 text-xs text-white/70 hover:bg-white/10">
            <span>Preview client portal</span><ExternalLink className="h-3 w-3" />
          </a>
          <div className="flex items-center gap-3 rounded-md px-3 py-2">
            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-[#2ECC9A] text-sm font-bold text-[#1A3A5C]">{avatar}</div>
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-medium">{user?.name || '—'}</div>
              <div className="truncate text-[10px] uppercase tracking-wider text-white/50">
                {user?.role || 'Staff'}{user?.marn ? ` · MARN ${user.marn}` : ''}
              </div>
            </div>
            <button className="text-white/50 hover:text-white" onClick={signOut} title="Sign out">
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </div>
      </aside>

      <div className="flex flex-1 flex-col overflow-hidden">
        <header className="flex h-16 items-center gap-4 border-b border-slate-200 bg-white px-6">
          <div className="relative flex-1 max-w-xl">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input placeholder="Search matters, clients, documents, policy alerts…" className="h-10 pl-9" />
          </div>
          <button className="flex items-center gap-2 rounded-md border border-slate-200 px-3 py-2 text-sm text-slate-600 hover:bg-slate-50">
            <HelpCircle className="h-4 w-4" />Help
          </button>
          <TimerWidget />
          <AlertsBell />
          <div className="flex items-center gap-2 rounded-md border border-slate-200 px-3 py-1.5 text-sm">
            <span className="text-slate-500">Firm:</span>
            <span className="font-medium text-[#1A3A5C]">{user?.firm || '—'}</span>
            <ChevronDown className="h-3 w-3 text-slate-400" />
          </div>
        </header>

        <div className="flex-1 overflow-y-auto">{children}</div>
      </div>
      <GuidedTourOverlay />
    </div>
  );
}
